import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../utils/init-firebase';

/**
 * Loads the PostcardMania design gallery via the listPostcardDesigns callable.
 * Shared by the SEND_POSTCARD node config form and the bulk postcard modal.
 *
 * @param {boolean} enabled Load only when true (e.g. modal open / form mounted)
 * @returns {{ designs, environment, balance, loading, error, reload }}
 *   designs: [{ designID, friendlyName, size, proofFront, proofBack,
 *               designFields, mailClasses, pricing }]
 *   environment: 'sandbox' | 'production' | null
 *   balance: PCM account balance in dollars, or null when unavailable
 */
export default function usePostcardDesigns(enabled = true) {
  const [designs, setDesigns] = useState([]);
  const [environment, setEnvironment] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadKey, setLoadKey] = useState(0);

  const reload = useCallback(() => setLoadKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable(functionsUsEast1, 'listPostcardDesigns');
        const { data } = await fn({});
        if (cancelled) return;
        if (data?.ok) {
          setDesigns(data.designs || []);
          setEnvironment(data.environment || null);
          setBalance(typeof data.balance === 'number' ? data.balance : null);
        } else {
          setError(data?.error || 'Could not load postcard designs');
          setEnvironment(data?.environment || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load postcard designs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, loadKey]);

  return { designs, environment, balance, loading, error, reload };
}

/** Lowest per-piece price for a mail class from a design's pricing tiers. */
export function priceForMailClass(pricing, mailClass) {
  if (!Array.isArray(pricing)) return null;
  const tiers = pricing.filter((p) => p.mailClass === mailClass);
  if (!tiers.length) return null;
  return tiers.reduce((min, t) => (t.price < min ? t.price : min), tiers[0].price);
}
